import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Segmented, Switch, Tooltip, Checkbox, Spin, Input, Tag, Space, message } from "antd";
import {
  BellOutlined,
  MessageOutlined,
  MobileOutlined,
  MailOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_MY_NUDGE_PREFERENCES } from "../../../graphql/query";
import { UPDATE_MY_NUDGE_PREFERENCES } from "../../../graphql/mutation";
import { UserContext } from "../../../amplify/authenticator-provider";
import CustomButton from "../../commonComponents/button";
import {
  CONTROL_TYPE,
  NUDGE_CATEGORIES,
  TEMPLATE_VARIABLES_BY_CATEGORY,
  getNudgePrefConfig,
} from "./nudgePrefsConfig";

/**
 * EAI-1598 — self-service per-user nudge notification preferences.
 *
 * DATA-DRIVEN: the backend returns every active nudge event; ./nudgePrefsConfig
 * decides which are user-configurable, their category/label/control-type, and
 * default template. This component renders purely off that config + the query,
 * so new nudge types (more OttoVin, or general EngagedAi notifications) are a
 * config change, not a component change. Codes not in the registry are skipped.
 *
 * Persisted per user (CompanyNudgeEvent row with assigned_user_id = me, tagged
 * extra_data.is_user_preference). Company/location config is the fallback.
 *
 * FREQUENCY control (e.g. OttoVin "Link opens"): Off -> isActive=false;
 * First -> isActive=true, every=false; Every -> isActive=true, every=true.
 */

const FREQUENCY_OPTIONS = [
  { label: "Off", value: "off" },
  { label: "First open only", value: "first" },
  { label: "Every open", value: "every" },
];

const FREQUENCY_HINTS = {
  off: "You won't be notified when a customer opens a link you sent.",
  first: "Notify me the first time a customer opens an OttoVin link.",
  every: "Notify me every time a customer opens the link.",
};

const DEFAULT_TIMING = {
  startDelay: 0,
  startDelayType: "MINUTES",
  frequency: 0,
  frequencyType: "MINUTES",
  reminderCount: 0,
};

const CHANNELS = [
  { key: "isSms", label: "SMS", icon: <MessageOutlined /> },
  { key: "isWebPush", label: "Push", icon: <MobileOutlined /> },
];

const safeParse = str => {
  try {
    return JSON.parse(str);
  } catch {
    return {};
  }
};

const readEveryView = extraData => {
  if (!extraData) return false;
  const data = typeof extraData === "string" ? safeParse(extraData) : extraData;
  return Boolean(data?.notify_every_page_view);
};

// EAI-1598 — the ottovin_settings DataService gate. Which categories a location
// has enabled, keyed by the category key used in nudgePrefsConfig.
// Extend this map as more location-gated categories are added.
const CATEGORY_FEATURE_SERVICE = {
  ottovin: "ottovin_settings",
};

// Read location feature flags off the user's PRIMARY location (user.location),
// NOT the location dropdown — these are the user's own personal preferences.
// A category is enabled only when its ottovin_settings-style DataServiceConfig
// is active AND run_params.enabled === true. Default false (hidden).
const readEnabledCategories = user => {
  const configs = user?.location?.dataServiceConfigs || [];
  const enabled = new Set();
  Object.entries(CATEGORY_FEATURE_SERVICE).forEach(([categoryKey, serviceName]) => {
    const cfg = configs.find(c => c?.dataService?.serviceName === serviceName);
    if (!cfg || !cfg.isActive) return;
    const params = typeof cfg.runParams === "string" ? safeParse(cfg.runParams) : cfg.runParams;
    if (params && params.enabled === true) enabled.add(categoryKey);
  });
  return enabled;
};

// Merge a query row with its registry config into an editable state object.
// Returns null when the code is not user-configurable (skipped from the tab).
const toState = row => {
  const { code } = row.nudgeEvent;
  const config = getNudgePrefConfig(code);
  if (!config) return null;
  const cne = row.companyNudgeEvent || {};
  return {
    nudgeEventId: row.nudgeEvent.id,
    code,
    config,
    label: config.label || row.nudgeEvent.title,
    hasOverride: Boolean(cne.id),
    isActive: Boolean(cne.isActive),
    isSms: cne.isSms ?? true,
    isWebPush: cne.isWebPush ?? true,
    isEmail: cne.isEmail ?? false,
    notifyEveryView: readEveryView(cne.extraData),
    // Pre-fill with the saved override, else the config default.
    firstTemplateText: cne.firstTemplateText || config.defaultTemplate || "",
    reminderTemplateText: cne.reminderTemplateText || config.defaultTemplate || "",
    parentTemplateText: cne.parentTemplateText || "",
    startDelay: cne.startDelay ?? DEFAULT_TIMING.startDelay,
    startDelayType: cne.startDelayType || DEFAULT_TIMING.startDelayType,
    frequency: cne.frequency ?? DEFAULT_TIMING.frequency,
    frequencyType: cne.frequencyType || DEFAULT_TIMING.frequencyType,
    reminderCount: cne.reminderCount ?? DEFAULT_TIMING.reminderCount,
  };
};

const NotificationsTab = ({ user }) => {
  const { user: userContext } = useContext(UserContext);
  const companyId = userContext?.company?.id;
  const assignedUserId = user?.id;

  const [rowsByCode, setRowsByCode] = useState({});
  const [dirty, setDirty] = useState(false);
  const textAreaRefs = useRef({});

  const [fetchPrefs, { loading }] = useLazyQuery(GET_MY_NUDGE_PREFERENCES, {
    fetchPolicy: "network-only",
    onCompleted: data => {
      const next = {};
      (data?.getCompanyNudgeEvent || []).forEach(row => {
        if (!row?.nudgeEvent) return;
        const state = toState(row);
        if (state) next[state.code] = state; // skip non-user-configurable codes
      });
      setRowsByCode(next);
      setDirty(false);
    },
  });

  const [savePrefs, { loading: saving }] = useMutation(UPDATE_MY_NUDGE_PREFERENCES);

  useEffect(() => {
    if (companyId && assignedUserId) {
      fetchPrefs({ variables: { companyId, assignedUserId } });
    }
  }, [companyId, assignedUserId, fetchPrefs]);

  // Location feature gate (read off the user's PRIMARY location). A category
  // with an entry in CATEGORY_FEATURE_SERVICE shows only when enabled here;
  // categories without a gate entry are always shown.
  const enabledCategories = useMemo(() => readEnabledCategories(user), [user]);

  // Group the resolved rows by category, preserving category + sortOrder order.
  // Skip any gated category the user's location hasn't enabled.
  const groupedCategories = useMemo(() => {
    return NUDGE_CATEGORIES.map(category => {
      const isGated = Object.prototype.hasOwnProperty.call(CATEGORY_FEATURE_SERVICE, category.key);
      if (isGated && !enabledCategories.has(category.key)) {
        return { category, rows: [] };
      }
      const rows = Object.values(rowsByCode)
        .filter(r => r.config.category === category.key)
        .sort((a, b) => (a.config.sortOrder || 0) - (b.config.sortOrder || 0));
      return { category, rows };
    }).filter(group => group.rows.length > 0);
  }, [rowsByCode, enabledCategories]);

  const patchRow = (code, patch) => {
    setRowsByCode(prev => {
      const existing = prev[code];
      if (!existing) return prev;
      return { ...prev, [code]: { ...existing, ...patch } };
    });
    setDirty(true);
  };

  const frequencyValue = row => {
    if (!row.isActive) return "off";
    return row.notifyEveryView ? "every" : "first";
  };

  const handleFrequencyChange = (code, value) => {
    if (value === "off") {
      patchRow(code, { isActive: false });
    } else {
      patchRow(code, { isActive: true, notifyEveryView: value === "every" });
    }
  };

  const insertVariable = (code, token) => {
    const el = textAreaRefs.current[code]?.resizableTextArea?.textArea;
    const current = rowsByCode[code]?.firstTemplateText || "";
    if (el && typeof el.selectionStart === "number") {
      const { selectionStart: start, selectionEnd: end } = el;
      const next = current.slice(0, start) + token + current.slice(end);
      patchRow(code, { firstTemplateText: next });
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      });
    } else {
      patchRow(code, { firstTemplateText: `${current}${token}` });
    }
  };

  const resetTemplate = code => {
    patchRow(code, { firstTemplateText: rowsByCode[code]?.config?.defaultTemplate || "" });
  };

  const handleSave = async () => {
    const inputs = Object.values(rowsByCode).map(r => ({
      nudgeEventId: r.nudgeEventId,
      startDelay: r.startDelay,
      startDelayType: r.startDelayType,
      frequency: r.frequency,
      frequencyType: r.frequencyType,
      firstTemplateText: r.firstTemplateText,
      reminderTemplateText: r.reminderTemplateText,
      parentTemplateText: r.parentTemplateText,
      isSms: r.isSms,
      isEmail: r.isEmail,
      isWebPush: r.isWebPush,
      isActive: r.isActive,
      reminderCount: r.reminderCount,
      notifyEveryPageView: r.notifyEveryView,
    }));
    try {
      const { data } = await savePrefs({
        variables: { myNudgePreferenceInputs: inputs },
      });
      const res = data?.updateMyNudgePreferences;
      if (res?.statusCode === 200) {
        message.success("Notification preferences saved");
        if (companyId && assignedUserId) {
          fetchPrefs({ variables: { companyId, assignedUserId } });
        }
      } else {
        message.error(res?.message || "Could not save preferences");
      }
    } catch (e) {
      message.error("Could not save preferences");
    }
  };

  // Channel checkboxes + editable template shown when a nudge is enabled.
  const renderDetails = row => {
    const variables = TEMPLATE_VARIABLES_BY_CATEGORY[row.config.category] || [];
    const isDefault = row.firstTemplateText === row.config.defaultTemplate;
    return (
      <div className="mt-5 bg-grey-50 border border-solid border-grey-200 rounded-lg p-4">
        <div className="mb-4">
          <p className="form-field-label mb-2">Deliver via</p>
          <div className="flex items-center gap-5">
            {CHANNELS.map(ch => (
              <Checkbox
                key={ch.key}
                checked={row[ch.key]}
                onChange={e => patchRow(row.code, { [ch.key]: e.target.checked })}
              >
                <span className="inline-flex items-center gap-1.5">
                  {ch.icon}
                  {ch.label}
                </span>
              </Checkbox>
            ))}
            <Tooltip title="Email delivery is coming soon">
              <span className="inline-flex">
                <Checkbox disabled>
                  <span className="inline-flex items-center gap-1.5">
                    <MailOutlined />
                    Email
                  </span>
                </Checkbox>
              </span>
            </Tooltip>
          </div>
        </div>

        {row.config.editableTemplate && (
          <>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor={`tmpl-${row.code}`} className="form-field-label !mb-0">
                Message
              </label>
              <button
                type="button"
                onClick={() => resetTemplate(row.code)}
                disabled={isDefault}
                className={`inline-flex items-center gap-1 text-xs bg-transparent border-0 p-0 ${
                  isDefault ? "text-grey-400 cursor-not-allowed" : "text-twLinkColor cursor-pointer"
                }`}
              >
                <UndoOutlined />
                Reset to default
              </button>
            </div>
            <Input.TextArea
              id={`tmpl-${row.code}`}
              ref={el => {
                textAreaRefs.current[row.code] = el;
              }}
              value={row.firstTemplateText}
              onChange={e => patchRow(row.code, { firstTemplateText: e.target.value })}
              autoSize={{ minRows: 3, maxRows: 6 }}
              maxLength={480}
              showCount
              placeholder="Write the message you receive…"
            />
            {variables.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="text-black-45 text-xs mr-1">Insert:</span>
                {variables.map(v => (
                  <Tooltip key={v.token} title={v.hint}>
                    <Tag
                      className="cursor-pointer m-0 select-none"
                      onClick={() => insertVariable(row.code, v.token)}
                    >
                      {v.token}
                    </Tag>
                  </Tooltip>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // A single nudge row: FREQUENCY (segmented) or TOGGLE (switch).
  const renderNudgeRow = row => {
    if (row.config.controlType === CONTROL_TYPE.FREQUENCY) {
      const value = frequencyValue(row);
      return (
        <div
          key={row.code}
          className="bg-white border border-solid border-grey-200 rounded-lg p-6 shadow-sm"
        >
          <p className="text-black-88 text-sm font-medium mb-1">{row.label}</p>
          {row.config.description && (
            <p className="text-black-45 text-xs mb-3">{row.config.description}</p>
          )}
          <Segmented
            options={FREQUENCY_OPTIONS}
            value={value}
            onChange={val => handleFrequencyChange(row.code, val)}
          />
          <p className="text-black-45 text-xs mt-2">{FREQUENCY_HINTS[value]}</p>
          {value !== "off" && renderDetails(row)}
        </div>
      );
    }
    // TOGGLE
    return (
      <div
        key={row.code}
        className="bg-white border border-solid border-grey-200 rounded-lg p-6 shadow-sm"
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-black-88 text-sm font-medium mb-0">{row.label}</p>
            {row.config.description && (
              <p className="text-black-45 text-xs mb-0">{row.config.description}</p>
            )}
          </div>
          <Switch
            checked={row.isActive}
            onChange={checked => patchRow(row.code, { isActive: checked })}
            aria-label={row.label}
          />
        </div>
        {row.isActive && renderDetails(row)}
      </div>
    );
  };

  if (loading && !Object.keys(rowsByCode).length) {
    return (
      <div className="pt-10 flex justify-center">
        <Spin />
      </div>
    );
  }

  return (
    <div className="pt-8 max-w-[760px]">
      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <span className="text-twLinkColor text-xl leading-none mt-0.5">
          <BellOutlined />
        </span>
        <div>
          <p className="text-black-88 font-semibold text-base mb-1">Notification Preferences</p>
          <p className="text-black-45 text-sm max-w-[560px]">
            Choose which alerts you get and customize the message. These apply to your account and
            override your dealership&apos;s defaults.
          </p>
        </div>
      </div>

      {groupedCategories.length === 0 && (
        <div className="border border-solid border-grey-200 rounded-xl p-8 text-center">
          <p className="text-black-45 text-sm mb-0">
            There are no notifications you can personalize right now.
          </p>
        </div>
      )}

      {/* Category sections — soft tinted panel; white row cards sit on top. */}
      {groupedCategories.map(({ category, rows }) => (
        <section
          key={category.key}
          className="bg-grey-50 border border-solid border-grey-200 rounded-xl p-6 mb-5"
        >
          <p className="text-black-88 font-semibold text-sm mb-1">{category.title}</p>
          {category.description && (
            <p className="text-black-45 text-xs mb-4">{category.description}</p>
          )}
          <div className="flex flex-col gap-4">{rows.map(renderNudgeRow)}</div>
        </section>
      ))}

      <p className="text-black-45 text-xs mb-6">
        These preferences apply to your account only. When you haven&apos;t set a preference, your
        dealership&apos;s default settings are used.
      </p>

      {/* Inline action row — matches the Security tab button pattern. */}
      {groupedCategories.length > 0 && (
        <Space className="mt-2">
          <CustomButton
            type="primary"
            htmlType="button"
            handleClick={handleSave}
            loading={saving}
            disabled={!dirty}
            btnText="Save preferences"
          />
        </Space>
      )}
    </div>
  );
};

export default NotificationsTab;
